import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/use-async';
import { Overview as OverviewData } from '../lib/types';
import { daysUntil, formatDateTime, relativeTime } from '../lib/format';
import { Badge, Card, Empty, Notice, PageHead, Stat } from '../components/ui';

export function Overview() {
  const { data, error, loading } = useAsync(() => api.get<OverviewData>('/api/admin/overview'));

  if (loading) return <p className="muted">Loading...</p>;
  if (error) return <Notice tone="danger">{error}</Notice>;
  if (!data) return null;

  const { statistics } = data;
  const suspended = data.credentials.filter((credential) => credential.state !== 'active');
  const expiringSoon = data.expiringKeys.filter((key) => daysUntil(key.expires_at) <= 30);
  const maxTimeline = Math.max(1, ...statistics.timeline.map((point) => point.total));

  return (
    <>
      <PageHead
        title="Overview"
        description="What went through the gateway in the last 24 hours and what needs attention."
      />

      {suspended.length ? (
        <Notice tone="warn" title="Suspended credentials">
          {suspended.map((credential) => (
            <span key={credential.id}>
              {credential.module_key} / {credential.label}
              {credential.suspended_until ? ` until ${formatDateTime(credential.suspended_until)}` : ''}{' '}
            </span>
          ))}
        </Notice>
      ) : null}

      {data.identityProblems.length ? (
        <Notice tone="warn" title="Open identity mappings">
          The mapping to the target API is missing for {data.identityProblems.length} people. Write accesses
          in their name are denied until that is resolved.
        </Notice>
      ) : null}

      <div className="grid cols-4" style={{ marginBottom: 20 }}>
        <Stat label="Requests (24 h)" value={statistics.total.toLocaleString('en-GB')} />
        <Stat
          label="Denied"
          value={statistics.denied.toLocaleString('en-GB')}
          note={statistics.total ? `${Math.round((statistics.denied / statistics.total) * 100)} %` : undefined}
        />
        <Stat label="Write operations" value={statistics.writes.toLocaleString('en-GB')} />
        <Stat
          label="Average duration"
          value={statistics.averageDurationMs === null ? '—' : `${statistics.averageDurationMs} ms`}
        />
      </div>

      <div className="grid cols-2">
        <Card title="Timeline" hint={`${statistics.hours} hours`}>
          {statistics.timeline.length ? (
            <div className="timeline">
              {statistics.timeline.map((point) => (
                <div
                  key={point.bucket}
                  className={point.denied > 0 ? 'bar denied' : 'bar'}
                  style={{ height: `${(point.total / maxTimeline) * 100}%` }}
                  title={`${formatDateTime(point.bucket)}: ${point.total} requests, ${point.denied} denied`}
                />
              ))}
            </div>
          ) : (
            <Empty>No requests logged yet.</Empty>
          )}
        </Card>

        <Card title="Inventory">
          <div className="grid cols-4">
            <Stat label="Modules" value={data.counts.modules} />
            <Stat label="Applications" value={data.counts.applications} />
            <Stat label="Users" value={data.counts.users} />
          </div>
        </Card>
      </div>

      <div className="grid cols-2">
        <Card title="Credentials in the pool" tight>
          {data.credentials.length ? (
            <table>
              <thead>
                <tr>
                  <th>Module</th>
                  <th>Credential</th>
                  <th>State</th>
                  <th className="num">Calls</th>
                  <th>Last used</th>
                </tr>
              </thead>
              <tbody>
                {data.credentials.map((credential) => (
                  <tr key={credential.id}>
                    <td className="mono">{credential.module_key}</td>
                    <td>{credential.label}</td>
                    <td>
                      <Badge tone={credential.state === 'active' ? 'ok' : 'warn'}>{credential.state}</Badge>
                    </td>
                    <td className="num">{Number(credential.use_count).toLocaleString('en-GB')}</td>
                    <td className="muted nowrap">{relativeTime(credential.last_used_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty>No credential stored yet.</Empty>
          )}
        </Card>

        <Card title="API keys expiring soon" tight>
          {expiringSoon.length ? (
            <table>
              <thead>
                <tr>
                  <th>Application</th>
                  <th>Key</th>
                  <th>Expires</th>
                  <th>Last used</th>
                </tr>
              </thead>
              <tbody>
                {expiringSoon.map((key) => (
                  <tr key={key.id}>
                    <td>{key.application_name}</td>
                    <td>{key.label}</td>
                    <td>
                      <Badge tone={daysUntil(key.expires_at) <= 7 ? 'danger' : 'warn'}>
                        in {daysUntil(key.expires_at)} days
                      </Badge>
                    </td>
                    <td className="muted">{relativeTime(key.last_used_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty>No key expires within the next 30 days.</Empty>
          )}
        </Card>
      </div>

      <div className="grid cols-2">
        <Card title="By module" tight>
          <table>
            <tbody>
              {statistics.byModule.map((entry) => (
                <tr key={entry.moduleKey}>
                  <td className="mono">{entry.moduleKey}</td>
                  <td className="num">{entry.total.toLocaleString('en-GB')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card title="By application" tight>
          <table>
            <tbody>
              {statistics.byApplication.map((entry) => (
                <tr key={entry.applicationName}>
                  <td>{entry.applicationName}</td>
                  <td className="num">{entry.total.toLocaleString('en-GB')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      <p className="muted">
        Individual operations are listed in the <Link to="/audit">audit log</Link>.
      </p>
    </>
  );
}
